import moment from 'moment'
import { ChartPlan } from 'src/charting/plan/chartPlan.types'
import { ExtendCurrency } from 'src/enums'
import {
  SpendChartPoint,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'
import { formatMonthDayRange } from 'src/utils/date.utils'

/**
 * Fixed-window presets shown as rows in each chart's date dropdown. The custom
 * range is handled separately via the calendar sub-view, so it is not part of
 * this union.
 */
export type ReportingPreset = 'this_month' | 'last_month'

/** A chart's active range — a fixed preset, or a user-picked custom range. */
export type ReportingRangeKind = ReportingPreset | 'custom'

export type ReportingDateRange = {
  since: moment.Moment
  until: moment.Moment
  preset: ReportingRangeKind
}

export type PanelGatedState =
  | { type: 'feature_disabled' } // Departments feature toggle is off
  | { type: 'not_entitled' } // Org is on Starter tier
  | { type: 'not_connected' } // Pro but no active ERP integration
  | { type: 'not_configured' } // Departments enabled but no teams have been created

/** Display order of the fixed presets in the dropdown. */
export const reportingPresetOrder: ReportingPreset[] = [
  'this_month',
  'last_month',
]

/** Default preset applied on load and by "Reset". */
export const DEFAULT_REPORTING_PRESET: ReportingPreset = 'this_month'

/** Localization keys for each fixed preset label (must exist in en-US.json). */
export const reportingPresetLabelKey: Record<ReportingPreset, string> = {
  this_month: 'This Month',
  last_month: 'Last Month',
}

/**
 * Resolves a fixed preset to its UTC calendar-month window (start of month to
 * end of month). UTC matches the backend's timestamp truncation. Unlike the
 * transactions date filter (which is browser-local), this is intentionally UTC.
 */
export const resolveReportingPresetWindow = (
  preset: ReportingPreset,
  now: moment.Moment = moment.utc(),
): ReportingDateRange => {
  switch (preset) {
    case 'this_month':
      return {
        since: now.clone().startOf('month'),
        until: now.clone().endOf('month'),
        preset,
      }
    case 'last_month': {
      const lastMonth = now.clone().subtract(1, 'month')
      return {
        since: lastMonth.clone().startOf('month'),
        until: lastMonth.clone().endOf('month'),
        preset,
      }
    }
  }
}

export interface ReportingPanelQueryParams {
  organizationId: string | undefined
  currency: ExtendCurrency
  enabled: boolean
  /** Shared, page-level range — every panel renders the same window. */
  dateRange: ReportingDateRange
  onNavigate?: (destination: string) => void
  resolveGatedDestination?: (
    type: PanelGatedState['type'],
    organizationId: string | undefined,
  ) => string
}

export interface RankedSpendRow {
  id: string
  rank: number
  label: string
  amountCents: number
  point: SpendChartPoint
}

const otherSeriesLabel = 'Other'

export const formatDate = (date: moment.Moment): string =>
  date.format('YYYY-MM-DD')

/**
 * Human-readable date-range sub-label, e.g. "May 20 – Jun 18, 2026". Used under
 * each preset row in the dropdown and on the custom trigger.
 */
export const formatReportingDateRange = (range: ReportingDateRange): string =>
  formatMonthDayRange(range.since, range.until)

export const createDefaultDateRange = (): ReportingDateRange =>
  resolveReportingPresetWindow(DEFAULT_REPORTING_PRESET)

const parseReportingDateParam = (
  value: string | null,
): moment.Moment | undefined => {
  if (value === null) return undefined

  const parsed = moment.utc(value, 'YYYY-MM-DD', true)
  return parsed.isValid() ? parsed : undefined
}

/**
 * Supports local QA links like
 * `/reporting?fromDate=2025-03-01&toDate=2025-05-31`. When both params are
 * valid ISO dates, they become the shared custom range shown in the page-level
 * date picker and sent to every dashboard chart query.
 */
export const createDateRangeFromSearch = (
  search: string,
): ReportingDateRange | undefined => {
  const params = new URLSearchParams(search)
  const since = parseReportingDateParam(params.get('fromDate'))
  const until = parseReportingDateParam(params.get('toDate'))

  if (since === undefined || until === undefined || since.isAfter(until)) {
    return undefined
  }

  return {
    since: since.startOf('day'),
    until: until.endOf('day'),
    preset: 'custom',
  }
}

export const formatMoney = (
  amountCents: number,
  currency: ExtendCurrency,
  locale = 'en-US',
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amountCents / 100)

export const formatCompactMoney = (
  amountCents: number,
  currency: ExtendCurrency,
  locale = 'en-US',
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amountCents / 100)

export const formatPercentage = (value: number, locale = 'en-US'): string =>
  new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(value)

// Synthetic "no real value" buckets the spend-dashboard API emits — the GL
// "uncategorized" bucket and the per-dimension "unknown_*" buckets. These should
// never headline a chart as its "top" series. Keyed off the stable API seriesKey
// (not the translated display label) so the check is locale-proof. The top-N
// overflow "other" bucket is handled separately in getTopSeriesStat.
export const isUnattributedSeriesKey = (
  key: string | null | undefined,
): boolean =>
  key == null ||
  key.trim() === '' ||
  key === 'uncategorized' ||
  key.startsWith('unknown_')

export const getTopSeriesStat = (
  plan: ChartPlan | undefined,
  excludedLabels: ReadonlySet<string> = new Set<string>(),
): { label: string; amountCents: number } | undefined => {
  if (!plan) return undefined

  const totalsBySeries = plan.categories
    .flatMap((category) => category.segments)
    .reduce<Map<string, number>>((acc, segment) => {
      acc.set(
        segment.seriesLabel,
        (acc.get(segment.seriesLabel) ?? 0) + segment.value,
      )
      return acc
    }, new Map())

  const getLargestSeries = (excluded: ReadonlySet<string>) =>
    Array.from(totalsBySeries.entries()).reduce<
      { label: string; amountCents: number } | undefined
    >((top, [label, amountCents]) => {
      if (excluded.has(label)) return top
      if (!top || amountCents > top.amountCents) return { label, amountCents }
      return top
    }, undefined)

  // Never headline a synthetic bucket — the GL "Uncategorized" bucket or any
  // per-dimension "Unknown" bucket. The caller passes these in as excludedLabels,
  // derived from the stable API seriesKey (see isUnattributedSeriesKey), so this
  // is locale-proof. Prefer a real named series; fall back to "Other" only if
  // that's all there is — but never a synthetic bucket (return undefined so the
  // panel shows total spend rather than "Top X: Uncategorized").
  const withOther = new Set<string>([otherSeriesLabel, ...excludedLabels])

  return getLargestSeries(withOther) ?? getLargestSeries(excludedLabels)
}

export const getInitials = (label: string): string => {
  const words = label.trim().split(/\s+/).filter(Boolean)

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase()
  }

  return label.slice(0, 2).toUpperCase()
}

export const hasSpendData = (chart: SpendChartView | undefined): boolean =>
  (chart?.points ?? []).some((point) => point.spendCents !== 0)
